import React, { useState, useRef } from 'react';
import { Modal, Button, Form, FormGroup, Label, Input, Alert, Collapse } from 'reactstrap';
import { Loader } from './Loader';
import './ImportModal.scss';

export const ImportModal = ({ isOpen, toggle, onImport, title = "Import Data" }) => {
    const [file, setFile] = useState(null);
    const [base64Data, setBase64Data] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showGuide, setShowGuide] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            convertToBase64(selectedFile);
        }
    };

    const convertToBase64 = (file) => {
        const reader = new FileReader();
        reader.onload = () => {
            setBase64Data(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
        if (!base64Data) {
            setResult({
                Status: 'error',
                Message: 'Please select a file to import'
            });
            return;
        }

        setIsLoading(true);
        setResult(null);

        try {
            const response = await onImport({ file: base64Data });
            setResult(response);
        } catch (error) {
            setResult({
                Status: 'error',
                Message: 'Import failed: ' + (error.message || 'Unknown error')
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setBase64Data('');
        setResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        toggle();
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    const downloadSample = () => {
        const link = document.createElement('a');
        link.href = process.env.REACT_APP_API_PUBLIC_URL + '/sample_import.xlsx';
        link.download = 'sample_import.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Check if this is a race kit import to show specific guide
    const isRaceKitImport = title.toLowerCase().includes('race kit');

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" keyboard={false} backdrop="static" className="import-modal">
            <div className="modal-header">
                <h5 className="modal-title mt-0" id="importModalLabel">{title}</h5>
                <button type="button" onClick={handleClose} className="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div className="modal-body">
                <Loader loading={isLoading} transparent />
                
                {/* Import Requirements Guide */}
                {isRaceKitImport && (
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0 text-primary">
                                <i className="mdi mdi-information-outline me-2"></i>
                                Import Requirements Guide
                            </h6>
                            <Button 
                                color="outline-primary" 
                                size="sm" 
                                onClick={() => setShowGuide(!showGuide)}
                            >
                                <i className={`mdi mdi-chevron-${showGuide ? 'up' : 'down'} me-1`}></i>
                                {showGuide ? 'Hide Guide' : 'Show Guide'}
                            </Button>
                        </div>
                        
                        <Collapse isOpen={showGuide}>
                            <div className="border rounded p-3 bg-light">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6 className="text-success mb-2">
                                            <i className="mdi mdi-check-circle me-1"></i>
                                            Required Columns
                                        </h6>
                                        <ul className="list-unstyled small mb-3">
                                            <li><strong>Name:</strong> Full Name OR First Name + Last Name</li>
                                            <li><strong>ID Number:</strong> Participant ID</li>
                                            <li><strong>Date of Birth:</strong> Valid date (no future dates)</li>
                                            <li><strong>Gender:</strong> Male, Female, M, or F</li>
                                            <li><strong>Bib Number:</strong> Unique within the race</li>
                                            <li><strong>Race:</strong> Must match active event race</li>
                                        </ul>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="text-info mb-2">
                                            <i className="mdi mdi-information me-1"></i>
                                            Optional Columns
                                        </h6>
                                        <ul className="list-unstyled small mb-3">
                                            <li><strong>Email:</strong> Valid email address (optional)</li>
                                            <li><strong>Mobile:</strong> Phone number (optional)</li>
                                            <li><strong>Nationality:</strong> 3-letter country code</li>
                                            <li><strong>Club:</strong> Club name</li>
                                            <li><strong>Bib Name:</strong> Name to display on bib</li>
                                            <li><strong>Bib Tag ID:</strong> RFID tag identifier</li>
                                            <li><strong>Expected Finish Time:</strong> Goal time (HH:MM:SS)</li>
                                            <li><strong>Parent Bib:</strong> Yes/No — second bib, same number</li>
                                            <li><strong>Remarks:</strong> Note for this kit (max 700 characters)</li>
                                            <li><strong>Highlight Remarks:</strong> Yes/No — show the note at issue time</li>
                                        </ul>
                                    </div>
                                </div>
                                
                                <div className="row">
                                    <div className="col-12">
                                        <h6 className="text-warning mb-2">
                                            <i className="mdi mdi-tshirt-crew me-1"></i>
                                            Tee Assignment Format
                                        </h6>
                                        <p className="small mb-2">
                                            Use columns with format: <code>Tee Size: [Tee Type Name]</code> or <code>Parent Tee Size: [Tee Type Name]</code>
                                        </p>
                                        <p className="small text-muted mb-0">
                                            Examples: <code>Tee Size: Event T-Shirt</code> with cell value "Large"<br/>
                                            <code>Parent Tee Size: Event T-Shirt</code> with cell value "Medium"
                                        </p>
                                        <p className="small text-muted mb-0 mt-2">
                                            A <code>Parent Tee Size:</code> column is recorded as the parent's shirt.
                                            It is independent of <code>Parent Bib</code> — a parent may take a bib
                                            without a tee, or a tee without a bib.
                                        </p>
                                    </div>
                                </div>
                                
                                <hr className="my-3" />
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <h6 className="text-danger mb-2">
                                            <i className="mdi mdi-alert-circle me-1"></i>
                                            Important Notes
                                        </h6>
                                        <ul className="list-unstyled small mb-0">
                                            <li>• Bib numbers must be unique per race</li>
                                            <li>• Date of birth cannot be in the future</li>
                                            <li>• Race must exist in active event</li>
                                            <li>• Sufficient tee stock required</li>
                                            <li>• Email and mobile are optional fields</li>
                                        </ul>
                                    </div>
                                                                            <div className="col-md-6">
                                            <h6 className="text-primary mb-2">
                                                <i className="mdi mdi-file-excel me-1"></i>
                                                File Requirements
                                            </h6>
                                            <ul className="list-unstyled small mb-0">
                                                <li>• Max size: 10MB</li>
                                                <li>• Formats: XLSX, XLS, CSV</li>
                                                <li>• Case-insensitive column headers</li>
                                            </ul>
                                            <div className="mt-2">
                                                <Button 
                                                    color="info" 
                                                    size="sm" 
                                                    onClick={downloadSample}
                                                    disabled={isLoading}
                                                >
                                                    <i className="mdi mdi-download me-1"></i>
                                                    Download Sample Format
                                                </Button>
                                            </div>
                                        </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                )}
                
                <Form>
                    <FormGroup>
                        <Label for="importFile">Select Excel File</Label>
                        <Input
                            type="file"
                            id="importFile"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            disabled={isLoading}
                        />
                        <small className="form-text text-muted">
                            Supported formats: XLSX, XLS, CSV (Max size: 10MB)
                        </small>
                    </FormGroup>

                    {file && (
                        <Alert color="info">
                            <strong>Selected file:</strong> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </Alert>
                    )}

                    {result && (
                        <div className="mt-3">
                            {result.Status === 'success' ? (
                                <Alert color="success">
                                    <h6 className="alert-heading">Import Successful!</h6>
                                    <p>{result.Message}</p>
                                    {result.Data && (
                                        <div>
                                            <p><strong>Imported:</strong> {result.Data.imported_count} records</p>
                                            <p><strong>Total rows:</strong> {result.Data.total_rows}</p>
                                            {result.Data.errors && result.Data.errors.length > 0 && (
                                                <div>
                                                    <p><strong>Errors:</strong></p>
                                                    <ul className="mb-0">
                                                        {result.Data.errors.map((error, index) => (
                                                            <li key={index}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <hr />
                                    <Button color="success" onClick={handleRefresh}>
                                        <i className="mdi mdi-refresh mr-1"></i>
                                        Click to Refresh
                                    </Button>
                                </Alert>
                            ) : (
                                <Alert color="danger">
                                    <h6 className="alert-heading">Import Failed</h6>
                                    <p>{result.Message}</p>
                                    {result.Errors && result.Errors.length > 0 && (
                                        <div>
                                            <p><strong>Errors:</strong></p>
                                            <ul className="mb-0">
                                                {result.Errors.map((error, index) => (
                                                    <li key={index}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </div>
                    )}
                </Form>
            </div>
            <div className="modal-footer">
                <Button color="secondary" onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button 
                    color="primary" 
                    onClick={handleSubmit} 
                    disabled={!file || isLoading}
                >
                    {isLoading ? (
                        <>
                            <i className="mdi mdi-loading mdi-spin mr-1"></i>
                            Importing...
                        </>
                    ) : (
                        <>
                            <i className="mdi mdi-upload mr-1"></i>
                            Import
                        </>
                    )}
                </Button>
            </div>
        </Modal>
    );
}; 